import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PhonePreview } from "@/components/common/PhonePreview";
import { Menu } from "lucide-react";
import type { CustomPageContentData } from "@/types/custom-page";
import {
  RichEditor,
  getEmptyRichEditorData,
  hasRichEditorContent,
  renderRichEditorContent,
} from "@/features/rich-editor";

export interface CustomPageFormValues {
  title: string;
  buttonTitle: string;
  content: string;
  contentData: CustomPageContentData;
  enabled: boolean;
}

interface CustomPageFormProps {
  defaultValues?: CustomPageFormValues;
  submitLabel: string;
  isSubmitting: boolean;
  onSubmit: (data: CustomPageFormValues) => Promise<void>;
}

const initialValues: CustomPageFormValues = {
  title: "",
  buttonTitle: "",
  content: "",
  contentData: getEmptyRichEditorData(),
  enabled: true,
};

const buildPreviewDocument = (content: string) => `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        padding: 0 0 32px;
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        color: #18181b;
        background:
          radial-gradient(circle at top, rgba(244, 244, 245, 0.9), transparent 38%),
          linear-gradient(180deg, #fcfcfd 0%, #ffffff 24%);
        line-height: 1.5;
        word-break: break-word;
      }

      img,
      iframe,
      video {
        max-width: 100%;
        border: 0;
      }

      table {
        width: 100%;
        border-collapse: collapse;
      }

      a {
        color: #111827;
        text-underline-offset: 0.2em;
      }

      p,
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      figure,
      blockquote {
        margin-block-start: 0;
        margin-block-end: 0;
      }
    </style>
  </head>
  <body>
    ${content || "<p style='color:#71717a;padding:0 16px;'>Your custom page content preview will appear here.</p>"}
  </body>
</html>`;

export const CustomPageForm: React.FC<CustomPageFormProps> = ({
  defaultValues,
  submitLabel,
  isSubmitting,
  onSubmit,
}) => {
  const resolvedDefaults = defaultValues ?? initialValues;
  const [useTitleAsButtonTitle, setUseTitleAsButtonTitle] = useState(
    resolvedDefaults.buttonTitle === resolvedDefaults.title,
  );
  const [isPreviewMenuOpen, setIsPreviewMenuOpen] = useState(true);

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<CustomPageFormValues>({
    defaultValues: resolvedDefaults,
  });

  const title = watch("title");
  const buttonTitle = watch("buttonTitle");
  const content = watch("content");
  const contentData = watch("contentData");
  const enabled = watch("enabled");
  const effectiveButtonTitle = useTitleAsButtonTitle ? title : buttonTitle;
  const renderedPreviewContent = useMemo(
    () => renderRichEditorContent(contentData, content),
    [content, contentData],
  );

  useEffect(() => {
    reset(resolvedDefaults);
    setUseTitleAsButtonTitle(
      resolvedDefaults.buttonTitle === resolvedDefaults.title,
    );
  }, [reset, resolvedDefaults]);

  useEffect(() => {
    if (useTitleAsButtonTitle) {
      setValue("buttonTitle", title, {
        shouldDirty: true,
      });
    }
  }, [setValue, title, useTitleAsButtonTitle]);

  const handleUseTitleChange = (checked: boolean) => {
    setUseTitleAsButtonTitle(checked);

    if (checked) {
      setValue("buttonTitle", title, {
        shouldDirty: true,
      });
    }
  };

  const togglePreviewMenu = () => {
    setIsPreviewMenuOpen((open) => !open);
  };

  const closePreviewMenu = () => {
    setIsPreviewMenuOpen(false);
  };

  const submit = async (data: CustomPageFormValues) => {
    if (!hasRichEditorContent(data.contentData)) {
      setError("contentData", {
        type: "required",
        message: "Page content is required.",
      });
      return;
    }

    await onSubmit({
      ...data,
      buttonTitle: useTitleAsButtonTitle ? data.title : data.buttonTitle,
    });
  };

  return (
    <div className="flex flex-col gap-10 lg:flex-row">
      <form onSubmit={handleSubmit(submit)} className="flex-1 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="custom-page-title">Title</Label>
          <Input
            id="custom-page-title"
            {...register("title", { required: true })}
            placeholder="Late Checkout Terms"
            aria-invalid={!!errors.title}
          />
        </div>

        <div className="space-y-3 rounded-xl border border-zinc-200 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-zinc-900">
                Use title as button title
              </div>
              <div className="text-sm text-zinc-500">
                Turn off to use a custom label in the guest app button.
              </div>
            </div>
            <Switch
              checked={useTitleAsButtonTitle}
              onCheckedChange={handleUseTitleChange}
            />
          </div>

          {!useTitleAsButtonTitle ? (
            <div className="space-y-2">
              <Label htmlFor="custom-page-button-title">Button title</Label>
              <Input
                id="custom-page-button-title"
                {...register("buttonTitle", { required: true })}
                placeholder="Late checkout"
                aria-invalid={!!errors.buttonTitle}
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white">
            <div className="border-b border-zinc-200 px-4 py-3">
              <div className="font-medium text-zinc-900">Page content</div>
              <div className="text-sm text-zinc-500">
                Build the page with structured blocks. HTML is generated from
                this content.
              </div>
            </div>
            <div className="min-h-[360px] px-4 py-3">
              <RichEditor
                value={contentData}
                placeholder="Start building the guest page..."
                onChange={(data, html) => {
                  setValue("contentData", data, { shouldDirty: true });
                  setValue("content", html, { shouldDirty: true });
                  if (hasRichEditorContent(data)) {
                    clearErrors("contentData");
                  }
                }}
              />
            </div>
          </div>
          {errors.contentData?.message ? (
            <p className="text-destructive text-sm">
              {errors.contentData.message}
            </p>
          ) : null}
          <p className="text-sm text-zinc-500">
            Use blocks to compose the page. The generated HTML will be sent
            together with the editor structure.
          </p>
        </div>

        <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-4">
          <div>
            <div className="font-medium text-zinc-900">Enabled</div>
            <div className="text-sm text-zinc-500">
              Disabled pages stay in admin but are hidden in the guest app.
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => setValue("enabled", checked)}
          />
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="new_default"
            size="xl"
            className="min-w-[180px]"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : submitLabel}
          </Button>
        </div>
      </form>

      <div className="flex flex-1 flex-col items-center">
        <div className="mb-4 text-center">
          <h3 className="text-2xl font-semibold text-zinc-900">
            Guest App Preview
          </h3>
          <p className="text-sm text-zinc-500">
            Live mobile preview of the page and how guests reach it from the
            burger menu.
          </p>
        </div>

        <PhonePreview
          serviceHeader={false}
          containerClassName="mx-auto min-h-[680px]"
        >
          <div className="relative flex h-[600px] flex-col overflow-hidden bg-white">
            <div className="relative flex items-center justify-center border-b border-zinc-100 px-4 py-4">
              <button
                type="button"
                className={`absolute left-4 flex size-10 items-center justify-center rounded-full transition-colors ${
                  isPreviewMenuOpen
                    ? "bg-zinc-900 text-white"
                    : "bg-zinc-100 text-zinc-700"
                }`}
                onClick={togglePreviewMenu}
                aria-label={
                  isPreviewMenuOpen ? "Close preview menu" : "Open preview menu"
                }
                aria-expanded={isPreviewMenuOpen}
              >
                <Menu className="size-4" />
              </button>
              <div className="max-w-[70%] text-center text-base font-semibold text-zinc-900">
                {title || "Custom Page"}
              </div>
            </div>

            <div className="relative flex-1 bg-white">
              <iframe
                title="Custom page mobile preview"
                srcDoc={buildPreviewDocument(renderedPreviewContent)}
                className="h-full w-full bg-white"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />

              <div
                className={`absolute inset-y-0 left-0 z-20 w-[68%] transform border-r border-zinc-100 bg-white shadow-xl transition-transform duration-300 ${
                  isPreviewMenuOpen ? "translate-x-0" : "-translate-x-full"
                }`}
              >
                <div className="px-4 pt-5 pb-6">
                  <div className="text-lg font-semibold text-zinc-900">
                    Guesteria
                  </div>
                </div>

                <div className="space-y-2 px-3">
                  <button
                    type="button"
                    className="w-full rounded-2xl px-3 py-3 text-left text-sm font-medium text-zinc-500"
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-2xl px-3 py-3 text-left text-sm font-medium text-zinc-500"
                  >
                    Services
                  </button>
                  <button
                    type="button"
                    className="w-full rounded-2xl px-3 py-3 text-left text-sm font-medium text-zinc-500"
                  >
                    Requests
                  </button>
                </div>

                <div className="mt-8 px-4">
                  <div className="mb-3 text-sm font-semibold text-zinc-900">
                    Guest Experience
                  </div>
                  <div className="h-px bg-zinc-100" />
                </div>

                <div className="px-3 pt-4">
                  <button
                    type="button"
                    className={`w-full rounded-2xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                      enabled
                        ? "bg-zinc-100 text-zinc-900"
                        : "text-zinc-400 line-through"
                    }`}
                    onClick={closePreviewMenu}
                  >
                    <span className="truncate">
                      {effectiveButtonTitle || "Button title"}
                    </span>
                  </button>
                </div>
              </div>

              {isPreviewMenuOpen ? (
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 z-10 w-[32%] bg-black/5"
                  onClick={closePreviewMenu}
                  aria-label="Close preview menu overlay"
                />
              ) : null}
            </div>
          </div>
        </PhonePreview>
      </div>
    </div>
  );
};
